<template>
  <gs-drawer
    :value="open"
    size="wide"
    close-label="Cancel"
    close-variant="ghost"
    :title="title"
    :subtitle="subtitle"
    @close="$emit('close')"
  >
    <div class="gs-element-drawer" :data-drawer="type">
      <div class="gs-element-drawer__form">
        <gs-field
          v-for="group in timeGroups"
          :key="group.key"
          :label="group.label"
          :error="group.error"
          :data-field="group.key"
        >
          <div class="gs-element-drawer__time">
            <span v-for="unit in group.units" :key="unit.key" class="gs-element-drawer__time-unit">
              <select
                class="gs-element-drawer__input"
                :data-select="unit.key"
                :aria-label="unit.label"
                v-model.number="form[unit.key]"
              >
                <option v-for="option in unit.options" :key="option" :value="option">
                  {{ option }}
                </option>
              </select>
              <span class="gs-element-drawer__time-suffix" aria-hidden="true">{{ unit.suffix }}</span>
            </span>
            <span class="gs-element-drawer__time-preview" :data-preview="group.key">
              {{ group.preview }}
            </span>
          </div>
        </gs-field>

        <gs-field
          v-if="type === 'banner'"
          :label="$t('frontend_groovevideo_videos_step3_banner')"
          hint="A wide image reads best. It sits across the bottom of the player."
          :error="errors.image"
          data-field="image"
        >
          <div class="gs-element-drawer__picker">
            <image-picker v-model="form.ctaBannerImage" :primary="true" name="local" />
          </div>
        </gs-field>

        <gs-field
          v-if="type === 'text'"
          :label="$t('frontend_groovevideo_videos_step3_text')"
          :error="errors.ctaname"
          data-field="ctaname"
        >
          <input
            type="text"
            class="gs-element-drawer__input"
            :class="{ 'gs-element-drawer__input--error': errors.ctaname }"
            :placeholder="$t('frontend_groovevideo_videos_step3_enter_the_headline')"
            v-model="form.ctaname"
          />
        </gs-field>

        <gs-field
          v-if="type === 'cta'"
          :label="$t('frontend_groovevideo_videos_step3_text')"
          :error="errors.headingText"
          data-field="headingText"
        >
          <input
            type="text"
            class="gs-element-drawer__input"
            :class="{ 'gs-element-drawer__input--error': errors.headingText }"
            :placeholder="$t('frontend_groovevideo_videos_step3_eg_webinar')"
            v-model="form.headingText"
          />
        </gs-field>

        <gs-field
          v-if="type === 'cta'"
          :label="$t('frontend_groovevideo_videos_step3_button_text')"
          :error="errors.buttonText"
          data-field="buttonText"
        >
          <input
            type="text"
            class="gs-element-drawer__input"
            :class="{ 'gs-element-drawer__input--error': errors.buttonText }"
            :placeholder="$t('frontend_groovevideo_videos_step3_register_today')"
            v-model="form.buttonText"
          />
        </gs-field>

        <div v-if="type === 'text'" class="gs-switch-row">
          <span class="gs-switch-row__text">
            <span class="gs-switch-row__label">Link this text</span>
            <span class="gs-switch-row__help">
              Viewers can click the overlay to open a page of yours.
            </span>
          </span>
          <gd-toggle v-model="form.hyperLinkCaption" />
        </div>

        <gs-field
          v-if="showUrl"
          :label="$t('frontend_groovevideo_videos_step3_url')"
          :error="urlError"
          data-field="url"
        >
          <input
            type="url"
            inputmode="url"
            class="gs-element-drawer__input"
            :class="{ 'gs-element-drawer__input--error': urlError }"
            :placeholder="urlPlaceholder"
            v-model="form[urlKey]"
          />
        </gs-field>

        <div v-if="showUrl" class="gs-switch-row">
          <span class="gs-switch-row__text">
            <span class="gs-switch-row__label">
              {{ $t('frontend_groovevideo_videos_step3_new_tab_toggle') }}
            </span>
            <span class="gs-switch-row__help">Keeps your video playing behind the new tab.</span>
          </span>
          <gd-toggle v-model="form.newTabToggle" />
        </div>

        <div v-if="type === 'banner'" class="gs-switch-row">
          <span class="gs-switch-row__text">
            <span class="gs-switch-row__label">Show a skip button</span>
            <span class="gs-switch-row__help">Lets viewers dismiss the banner.</span>
          </span>
          <gd-toggle v-model="form.showSkipButton" />
        </div>
      </div>

      <div class="gs-element-drawer__footer">
        <gs-button variant="primary" data-action="save" @click="$emit('save')">
          {{ saveLabel }}
        </gs-button>
      </div>
    </div>
  </gs-drawer>
</template>

<script>
import ImagePicker from '@groovepages/gd-ui-kit/components/ImagePickerModal';
import { toSeconds, formatTime } from './timeFields';

// Every element type is stored in one `call_to_actions` array, and each one
// carries its own subset of the same form object. The drawer renders that
// subset; the panel owns the object, the validation and the save.
const COPY = {
  banner: {
    add: 'Add banner',
    edit: 'Edit banner',
    subtitle: 'Show an image with a link at a chosen time.',
  },
  text: {
    add: 'Add text overlay',
    edit: 'Edit text overlay',
    subtitle: 'A line of text across the video, with an optional link.',
  },
  cta: {
    add: 'Add call to action',
    edit: 'Edit call to action',
    subtitle: 'A heading and a button over the video.',
  },
};

const range = count => Array.from({ length: count }, (unused, index) => index);

export default {
  name: 'ElementDrawer',

  components: { ImagePicker },

  props: {
    open: {
      type: Boolean,
      default: false,
    },
    // 'banner' | 'text' | 'cta'
    type: {
      type: String,
      default: 'banner',
    },
    // The panel's `localmodel`, shared by reference so the opt-in modal and the
    // drawer keep reading the same draft.
    form: {
      type: Object,
      required: true,
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
    editing: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    copy() {
      return COPY[this.type] || COPY.banner;
    },

    title() {
      return this.editing ? this.copy.edit : this.copy.add;
    },

    subtitle() {
      return this.copy.subtitle;
    },

    saveLabel() {
      return this.editing ? 'Save changes' : this.copy.add;
    },

    // A banner shows at one moment; the two overlays show across a range.
    hasTillTime() {
      return this.type !== 'banner';
    },

    timeGroups() {
      const groups = [
        {
          key: 'from',
          label: this.hasTillTime ? 'Display from' : 'Display at',
          error: this.errors.time || '',
          units: this.unitsFor(['hours', 'minutes', 'seconds']),
          preview: formatTime(
            toSeconds({
              hours: this.form.hours,
              minutes: this.form.minutes,
              seconds: this.form.seconds,
            })
          ),
        },
      ];

      if (this.hasTillTime) {
        groups.push({
          key: 'till',
          label: 'Display till',
          error: this.errors.till || '',
          units: this.unitsFor(['till_hours', 'till_minute', 'till_seconds']),
          preview: formatTime(
            toSeconds({
              hours: this.form.till_hours,
              minutes: this.form.till_minute,
              seconds: this.form.till_seconds,
            })
          ),
        });
      }

      return groups;
    },

    // The text overlay only asks for a URL once the viewer can click it.
    showUrl() {
      return this.type !== 'text' || Boolean(this.form.hyperLinkCaption);
    },

    urlKey() {
      return this.type === 'text' ? 'optional_url' : 'url';
    },

    urlError() {
      return this.type === 'text' ? this.errors.optional_url || '' : this.errors.url || '';
    },

    urlPlaceholder() {
      return this.type === 'banner' ? 'e.g. https://www.yoursite.com' : 'e.g. https://www.site.com';
    },
  },

  methods: {
    unitsFor(keys) {
      const shape = [
        { suffix: 'H', label: 'Hours', options: range(24) },
        { suffix: 'M', label: 'Minutes', options: range(60) },
        { suffix: 'S', label: 'Seconds', options: range(60) },
      ];
      return shape.map((unit, index) => ({ ...unit, key: keys[index] }));
    },
  },
};
</script>

<style scoped>
.gs-element-drawer {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.gs-element-drawer__form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 560px;
}

.gs-element-drawer__input {
  display: block;
  width: 100%;
  min-height: 40px;
  padding: 10px 12px;
  color: var(--gs-foreground);
  background: var(--gs-input);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  font-size: 14px;
  line-height: 1.4;
}

.gs-element-drawer__input:focus,
.gs-element-drawer__input:focus-within {
  border-color: var(--gs-primary);
  outline: none;
  box-shadow: var(--gs-primary-shadow);
}

.gs-element-drawer__input--error {
  border-color: var(--gs-danger);
}

.gs-element-drawer__time {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.gs-element-drawer__time-unit {
  display: inline-flex;
  gap: 6px;
  align-items: center;
}

.gs-element-drawer__time-unit .gs-element-drawer__input {
  width: 84px;
  font-variant-numeric: tabular-nums;
}

.gs-element-drawer__time-suffix {
  color: var(--gs-muted-foreground);
  font-size: 13px;
  font-weight: 600;
}

.gs-element-drawer__time-preview {
  padding: 4px 8px;
  color: var(--gs-muted-foreground);
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-sm);
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.gs-element-drawer__picker {
  text-align: left;
}

.gs-element-drawer__picker ::v-deep #gp-styleEditor-chooseImage {
  flex: auto;
  width: 300px;
  max-width: 100%;
  color: var(--gs-foreground);
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-element-drawer__picker ::v-deep .imagePicker .pl-4 {
  text-align: left;
}

.gs-switch-row {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  justify-content: space-between;
  max-width: 560px;
  padding: 14px 16px;
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-switch-row__text {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.gs-switch-row__label {
  color: var(--gs-foreground);
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
}

.gs-switch-row__help {
  max-width: 56ch;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  line-height: 1.5;
}

/* The kit's drawer body keeps a 32px bottom padding, which a sticky footer
   would stop short of; the footer owns that inset instead. */
.gs-drawer ::v-deep .gs-drawer__body {
  padding-bottom: 0;
}

.gs-element-drawer__footer {
  display: flex;
  position: sticky;
  bottom: 0;
  z-index: 2;
  gap: 10px;
  justify-content: flex-end;
  margin: 4px -28px 0;
  padding: 16px 28px 24px;
  background: var(--gs-surface);
  border-top: 1px solid var(--gs-border);
}

@media (max-width: 640px) {
  .gs-element-drawer__footer {
    margin: 4px -18px 0;
    padding: 14px 18px 18px;
  }
}
</style>
